import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Check, Package, Truck, Home } from "lucide-react";
import api, { formatPrice } from "../lib/api";

const STAGES = [
    { id: "paid", label: "Confirmed", icon: Check },
    { id: "processing", label: "Preparing", icon: Package },
    { id: "shipped", label: "On its way", icon: Truck },
    { id: "delivered", label: "Delivered", icon: Home },
];

const ORDER = ["pending", "paid", "processing", "shipped", "delivered"];

export default function OrderTracking() {
    const { id } = useParams();
    const [order, setOrder] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get(`/orders/${id}`)
            .then(({ data }) => setOrder(data))
            .finally(() => setLoading(false));
    }, [id]);

    if (loading) return <div className="grid min-h-[60vh] place-items-center text-gold">Loading…</div>;
    if (!order) return <div className="grid min-h-[60vh] place-items-center text-white/60">Order not found</div>;

    const currentIdx = Math.max(0, ORDER.indexOf(order.status));

    return (
        <div className="bg-obsidian" data-testid="order-tracking-page">
            <div className="mx-auto max-w-[1100px] px-6 py-12 lg:px-12 lg:py-20">
                <Link to="/account?tab=orders" className="text-xs uppercase tracking-widest text-white/50 hover:text-gold">
                    ← Back to orders
                </Link>
                <p className="mt-6 text-[11px] uppercase tracking-[0.4em] text-gold">Order · {order.id.slice(0, 8)}</p>
                <h1 className="mt-2 font-display text-4xl text-white sm:text-5xl">Tracking your piece</h1>

                {/* Timeline */}
                <div className="mt-12 border border-border-dim p-6 sm:p-8">
                    {/* Mobile: vertical timeline */}
                    <div className="space-y-5 sm:hidden">
                        {STAGES.map((s) => {
                            const reached = ORDER.indexOf(s.id) <= currentIdx;
                            const Icon = s.icon;
                            return (
                                <div key={s.id} className="flex items-center gap-4">
                                    <div
                                        className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full border-2 ${
                                            reached ? "border-gold bg-gold text-obsidian" : "border-border-dim text-white/30"
                                        }`}
                                    >
                                        <Icon className="h-4 w-4" />
                                    </div>
                                    <p
                                        className={`text-[12px] uppercase tracking-[0.2em] ${
                                            reached ? "text-gold" : "text-white/40"
                                        }`}
                                    >
                                        {s.label}
                                    </p>
                                </div>
                            );
                        })}
                    </div>
                    {/* Desktop: horizontal timeline */}
                    <div className="hidden items-start justify-between gap-4 sm:flex">
                        {STAGES.map((s) => {
                            const reached = ORDER.indexOf(s.id) <= currentIdx;
                            const Icon = s.icon;
                            return (
                                <div key={s.id} className="flex flex-1 flex-col items-center text-center">
                                    <div
                                        className={`flex h-12 w-12 items-center justify-center rounded-full border-2 transition-colors ${
                                            reached ? "border-gold bg-gold text-obsidian" : "border-border-dim text-white/30"
                                        }`}
                                    >
                                        <Icon className="h-5 w-5" />
                                    </div>
                                    <p
                                        className={`mt-3 text-[11px] uppercase tracking-[0.2em] ${
                                            reached ? "text-gold" : "text-white/40"
                                        }`}
                                    >
                                        {s.label}
                                    </p>
                                </div>
                            );
                        })}
                    </div>
                    {order.tracking_number && (
                        <div className="mt-8 border-t border-border-dim pt-6 text-center">
                            <p className="text-xs uppercase tracking-widest text-white/40">Tracking number</p>
                            <p className="mt-1 font-display text-xl text-gold sm:text-2xl break-all">{order.tracking_number}</p>
                        </div>
                    )}
                </div>

                {/* Summary */}
                <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-3">
                    <div className="border border-border-dim bg-surface p-6 lg:col-span-2">
                        <h2 className="font-display text-2xl text-white">Pieces</h2>
                        <div className="mt-4 space-y-4">
                            {order.items.map((it) => (
                                <div key={it.product_id} className="flex gap-4 border-b border-border-dim pb-4 last:border-b-0">
                                    <img src={it.product?.images?.[0]} alt="" className="h-20 w-16 object-cover" />
                                    <div className="flex-1">
                                        <p className="font-display text-lg text-white">{it.product?.name}</p>
                                        <p className="text-xs text-white/40">
                                            {[it.size, it.color].filter(Boolean).join(" / ")} × {it.quantity}
                                        </p>
                                    </div>
                                    <p className="text-sm text-gold">{formatPrice(it.line_total)}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                    <div className="border border-border-dim bg-surface p-6">
                        <h2 className="font-display text-2xl text-white">Total</h2>
                        <div className="mt-4 space-y-2 text-sm">
                            <Row label="Subtotal" value={formatPrice(order.subtotal)} />
                            {order.discount > 0 && <Row label="Discount" value={`- ${formatPrice(order.discount)}`} />}
                            <Row label="Shipping" value={order.shipping === 0 ? "Free" : formatPrice(order.shipping)} />
                            <Row label="Tax" value={formatPrice(order.tax)} />
                            <div className="mt-3 flex items-baseline justify-between border-t border-border-dim pt-3">
                                <span className="text-[11px] uppercase tracking-[0.25em] text-white/60">Total</span>
                                <span className="font-display text-3xl text-gold">{formatPrice(order.total)}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

function Row({ label, value }) {
    return (
        <div className="flex items-baseline justify-between">
            <span className="text-white/60">{label}</span>
            <span className="text-white">{value}</span>
        </div>
    );
}
